// src/pages/PlanBillingPage.js

import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Typography
} from '@mui/material'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'
import { normalizePlanData } from '../utils/planUtils'

const StatBadge = ({ label, value, icon }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      borderColor: 'rgba(148,163,184,0.35)'
    }}
  >
    {icon}
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '.04em' }}>
        {label}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
  </Paper>
)

const PlanBillingPage = () => {
  const { organization, currentUser } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [planData, setPlanData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)

  useEffect(() => {
    if (!organization?.id) {
      setLoading(false)
      return
    }

    const orgRef = doc(db, 'organizations', organization.id)
    const unsubscribe = onSnapshot(
      orgRef,
      (snap) => {
        if (snap.exists()) {
          const normalized = normalizePlanData({ id: snap.id, ...snap.data() })
          setPlanData(normalized)
        }
        setLoading(false)
      },
      (error) => {
        console.error('Plan & billing load error:', error)
        setLoadError(t('planBilling.loadError'))
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [organization?.id, t])

  const usage = useMemo(() => {
    if (!planData) return { percent: 0, used: 0, limit: 0, remaining: 0 }
    const used = planData.responsesUsed ?? 0
    const limit = planData.responseLimit ?? 0
    const percent = limit > 0 ? Math.min((used / limit) * 100, 100) : 0
    return {
      percent,
      used,
      limit,
      remaining: Math.max(limit - used, 0)
    }
  }, [planData])

  const resetLabel = useMemo(() => {
    if (!planData?.responseResetAt) return t('planBilling.notAvailable')
    return planData.responseResetAt.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    })
  }, [planData?.responseResetAt, t])

  const billingInfo = currentUser?.billingInfo?.trim()
  const planName = planData?.planName || t('planBilling.freePlan')

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
      <Box
        sx={{
          maxWidth: 720,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5
        }}
      >
        {/* Page title / description */}
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t('planBilling.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('planBilling.subtitle')}
          </Typography>
        </Box>

        {/* Social style main "card" */}
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3,
            p: { xs: 2.5, md: 3 },
            borderColor: 'rgba(148,163,184,0.24)',
            backgroundColor: '#ffffff',
            boxShadow: '0 10px 30px rgba(15,23,42,0.06)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          {/* Card header like a social profile header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background:
                    'linear-gradient(135deg, rgba(59,130,246,0.16), rgba(37,99,235,0.22))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  fontWeight: 700,
                  color: '#1e293b'
                }}
              >
                {organization?.name?.[0]?.toUpperCase() || 'E'}
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {organization?.name || t('planBilling.currentPlan', { plan: planName })}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                  <Chip
                    size="small"
                    label={planName}
                    sx={{
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 600
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {t('planBilling.planDescription')}
                  </Typography>
                </Stack>
              </Box>
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/profile')}
              startIcon={<TrendingUpIcon />}
              sx={{
                borderRadius: 999,
                px: 2.5,
                py: 0.75,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {t('planBilling.upgrade')}
            </Button>
          </Box>

          {/* Usage section like an "activity" bar */}
          <Box sx={{ mt: 1 }}>
            {loading ? (
              <Skeleton variant="rounded" height={26} />
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {t('planBilling.responses')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('planBilling.responsesUsed', { used: usage.used, limit: usage.limit })}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={usage.percent}
                  sx={{
                    height: 10,
                    borderRadius: 999,
                    backgroundColor: 'rgba(148,163,184,0.35)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 999
                    }
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 1
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {t('planBilling.usageHint')}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Stats row like reactions / meta info under a post */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.5,
              mt: 0.5
            }}
          >
            <Box sx={{ flex: 1 }}>
              <StatBadge
                label={t('planBilling.remaining')}
                value={loading ? '—' : usage.remaining}
                icon={<SecurityRoundedIcon color="primary" />}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <StatBadge
                label={t('planBilling.used')}
                value={loading ? '—' : usage.used}
                icon={<TrendingUpIcon color="action" />}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <StatBadge
                label={t('planBilling.limit')}
                value={loading ? '—' : usage.limit}
                icon={<AccessTimeIcon color="action" />}
              />
            </Box>
          </Box>
        </Paper>

        {/* Billing "comments" style card */}
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3,
            p: { xs: 2.5, md: 3 },
            borderColor: 'rgba(148,163,184,0.24)',
            backgroundColor: '#ffffff',
            boxShadow: '0 6px 18px rgba(15,23,42,0.04)'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1.5,
              mb: 1.5
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CreditCardIcon color="primary" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t('planBilling.billingDetails')}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/profile')}
              sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 600 }}
            >
              {t('planBilling.editBilling')}
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {billingInfo || t('planBilling.billingPlaceholder')}
          </Typography>
          {loadError && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              {loadError}
            </Typography>
          )}
        </Paper>
      </Box>
    </Container>
  )
}

export default PlanBillingPage
